import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { cloneDeep, sortBy } from 'lodash-es';

import { Invitation, InvitationAccessLevel, InvitationStatusType } from '@vi-iot/client';

import { AppState } from '../app.state';

import { InvitationsState } from './invitations.state';

export namespace InvitationsSelectors {
  export const invitations = (state: AppState): InvitationsState => state.invitations;

  export function getState(): MemoizedSelector<
    AppState,
    InvitationsState,
    DefaultProjectorFn<InvitationsState>
  > {
    return createSelector(invitations, (state: InvitationsState) => state);
  }

  export function getIncomingCount(): MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > {
    return createSelector(invitations, (state: InvitationsState) => state.incomingCount);
  }

  export function getPendingTransferInstallationInvitations(): MemoizedSelector<
    AppState,
    Invitation[],
    DefaultProjectorFn<Invitation[]>
  > {
    return createSelector(
      invitations,
      (state: InvitationsState) => state.transferInstallationPendingInvitations
    );
  }

  export function getAreInvitationsLoaded(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(invitations, (state: InvitationsState) => state.loaded);
  }

  export function getInstallationRoleManagementInvitations(
    installationId: string | number
  ): MemoizedSelector<AppState, Invitation[], DefaultProjectorFn<Invitation[]>> {
    return createSelector(invitations, (state: InvitationsState) => {
      let roleManagementInvitations = [];
      let invitationsAsArray = cloneDeep(state.buildingManagerInvitations);

      invitationsAsArray = invitationsAsArray.filter(
        (entry) => entry.installationId === installationId
      );
      invitationsAsArray = sortBy(invitationsAsArray, (entry) => entry.editedAt);
      invitationsAsArray.reverse();

      const newestOperatorInvitation = invitationsAsArray.filter(
        (entry) => entry.accessLevel === InvitationAccessLevel.Operator
      )[0];
      if (
        newestOperatorInvitation &&
        newestOperatorInvitation.status !== InvitationStatusType.Canceled
      ) {
        roleManagementInvitations = roleManagementInvitations.concat([newestOperatorInvitation]);
      }

      const newestServiceContractorInvitation = invitationsAsArray.filter(
        (entry) => entry.accessLevel === InvitationAccessLevel.ServiceContractor
      )[0];
      if (
        newestServiceContractorInvitation &&
        newestServiceContractorInvitation.status !== InvitationStatusType.Canceled
      ) {
        roleManagementInvitations = roleManagementInvitations.concat([
          newestServiceContractorInvitation,
        ]);
      }

      return roleManagementInvitations;
    });
  }

  export function getAcceptedDateInvitation(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(invitations, (state: InvitationsState) => {
      const invitation = state?.userInvitations[0];

      return invitation &&
        invitation.status === InvitationStatusType.Accepted &&
        invitation.editedAt
        ? invitation.editedAt
        : undefined;
    });
  }

  export const incomingInvitations = createSelector(invitations, (state): Invitation[] =>
    state.invitationsIds.incoming
      .map((id) => state.invitations[id])
      .filter((invitation) => invitation)
  );

  export function getCommercialInvitationStatus(
    token: string
  ): MemoizedSelector<AppState, InvitationStatusType, DefaultProjectorFn<InvitationStatusType>> {
    return createSelector(
      invitations,
      (state): InvitationStatusType => state.commercialInvitations[token]?.status
    );
  }
}
